// Contexts
import React from 'react';
import { ProductCardsProvider } from './contexts';

// Hooks
import { useChannelRepository } from './hooks';

// Module Components
import DirectionBoundary from './components/DirectionBoundary';
import EnvironmentNotice from './components/EnvironmentNotice';

// Views
import { ProductCardsListView } from './views';

// Types
import { ProductCardsRepository } from './types';

interface ProductCardsProps {
  /**
   * The persistence port. Left unset in the app, where the HTTP repository for
   * the selected environment is built here (ADR-001 — the adapter landed
   * 2026-08-18); supplied by tests to stay off the network.
   */
  repository?: ProductCardsRepository;
}

/**
 * Product Cards module — route entry for `/gms/product_cards`.
 *
 * A card is a display template: which product fields appear on it, in what
 * order, and in which of three regions (photo overlay, info stack, footer). The
 * module is self-contained — its components, state, selectors, services and
 * types all live under this folder; the only wiring outside it is the route
 * registration in `src/App.tsx` and the navigation entry in
 * `src/Components/Templates/MenuHelper.ts`.
 *
 * See `okf/product-cards/architecture.md` for the layer boundaries and the
 * persistence seam.
 */
const ProductCards = ({ repository }: ProductCardsProps) => {
  const channelRepository = useChannelRepository();
  const activeRepository = repository ?? channelRepository;

  // No environment selected: there is no channel to address a request to, so
  // say so instead of showing an empty listing or a browser-local store.
  if (!activeRepository) {
    return (
      <DirectionBoundary>
        <EnvironmentNotice />
      </DirectionBoundary>
    );
  }

  return (
    <DirectionBoundary>
      <ProductCardsProvider repository={activeRepository}>
        <ProductCardsListView />
      </ProductCardsProvider>
    </DirectionBoundary>
  );
};

export default ProductCards;
